/**
 * ProfileShareDialog
 *
 * Dialog for generating and managing public share links for the profile.
 */

import { useState, useCallback } from 'react';
import { X, Copy, Check, Link2, Globe, CalendarDays, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { ProfileSettingsUI } from '@/db/profiles';

interface ProfileShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  settings: ProfileSettingsUI;
  onUpdate: (updates: Partial<ProfileSettingsUI>) => void;
  onGenerateLink: () => Promise<string | null>;
}

export function ProfileShareDialog({
  isOpen,
  onClose,
  settings,
  onUpdate,
  onGenerateLink,
}: ProfileShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(
    settings.publicShareToken
      ? `${window.location.origin}/p/${settings.publicShareToken}`
      : null
  );

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const url = await onGenerateLink();
      if (url) {
        setShareUrl(url);
      }
    } finally {
      setGenerating(false);
    }
  }, [onGenerateLink]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-primary">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-accent-violet/10 flex items-center justify-center">
              <Link2 className="w-4 h-4 text-accent-violet" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-text-primary">
                Condividi profilo
              </h3>
              <p className="text-[10px] text-text-quaternary">
                Genera un link pubblico per il tuo profilo
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Share Link */}
          {shareUrl ? (
            <div>
              <label className="block text-[10px] text-text-quaternary uppercase tracking-wider font-medium mb-1.5">
                Link di condivisione
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[11px] text-text-secondary truncate font-mono">
                  {shareUrl}
                </div>
                <button
                  onClick={handleCopy}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-colors',
                    copied
                      ? 'bg-accent-emerald/10 text-accent-emerald'
                      : 'bg-accent-violet/10 text-accent-violet hover:bg-accent-violet/20'
                  )}
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Copiato</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copia</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <Globe className="w-8 h-8 text-text-quaternary mx-auto mb-2" />
              <p className="text-[12px] text-text-secondary mb-1">
                Nessun link generato
              </p>
              <p className="text-[10px] text-text-quaternary mb-4">
                Genera un link per condividere il tuo profilo pubblicamente
              </p>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-violet text-white text-[12px] font-medium hover:bg-accent-violet-light transition-colors disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Generazione...</span>
                  </>
                ) : (
                  <>
                    <Link2 className="w-3.5 h-3.5" />
                    <span>Genera link</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Visibility toggles */}
          {shareUrl && (
            <div className="pt-3 border-t border-border-primary">
              <p className="text-[10px] text-text-quaternary uppercase tracking-wider font-medium mb-3">
                Contenuto visibile pubblicamente
              </p>

              <div className="space-y-2">
                <ShareToggle
                  icon={<CalendarDays className="w-3.5 h-3.5" />}
                  label="Calendario"
                  checked={settings.publicShowCalendar}
                  onChange={(v) => onUpdate({ publicShowCalendar: v })}
                />
              </div>
            </div>
          )}

          {/* Regenerate / disable */}
          {shareUrl && (
            <div className="flex items-center justify-between pt-3 border-t border-border-primary">
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="text-[11px] text-text-tertiary hover:text-text-secondary transition-colors disabled:opacity-50"
              >
                {generating ? 'Rigenerazione...' : 'Rigenera link'}
              </button>
              <button
                onClick={() => {
                  onUpdate({ publicShareEnabled: false, publicShareToken: null });
                  setShareUrl(null);
                }}
                className="text-[11px] text-accent-rose hover:text-accent-rose/80 transition-colors"
              >
                Disabilita condivisione
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Share Toggle ---

function ShareToggle({
  icon,
  label,
  checked,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <div className="text-text-tertiary">{icon}</div>
      <span className="flex-1 text-[11px] font-medium text-text-primary">{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full transition-colors duration-200',
          checked ? 'bg-accent-violet' : 'bg-surface-tertiary'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block h-3 w-3 mt-0.5 transform rounded-full bg-white shadow-sm transition-transform duration-200',
            checked ? 'translate-x-3.5' : 'translate-x-0.5'
          )}
        />
      </button>
    </div>
  );
}
